import { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { searchAPI, breedsAPI, categoriesAPI } from '@/services/api';
import { Search, MapPin, Star, ChevronLeft, ChevronRight, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createTranslationMap } from '@/utils/i18nHelpers';
import SaveSearchButton from '../components/SaveSearchButton';
import { resolveSearchTab, resolveUrl, injectAds } from "@/utils/adUtil";
import AdSlot from "@/components/AdSlot";

export default function SearchResultsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
	const location = useLocation();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [type, setType] = useState(searchParams.get('type') || 'all');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totals, setTotals] = useState({
		horse_total: 0,
		product_total: 0,
		transport_total: 0,
		school_total: 0,
		stable_total: 0,
		trainer_total: 0,
		event_total: 0,
		vet_total: 0,
		total: 0
	});
  const [breedMap, setBreedMap] = useState({});
  const [catMap, setCatMap] = useState({});

  useEffect(() => {
    breedsAPI.list()
			.then(r => setBreedMap(createTranslationMap(r.data || [], i18n.language)))
			.catch(() => {});
    categoriesAPI.list()
			.then(r => setCatMap(createTranslationMap(r.data || [], i18n.language)))
			.catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      setType(searchParams.get('type') || 'all');
      setPage(parseInt(searchParams.get('page') || '1'));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!query.trim()) { setLoading(false); return; }
    setLoading(true);
    searchAPI.unified({ q: query, type, page, limit: 11 })
      .then(r => {
        setResults(injectAds(r.data.items || [], page));
        setTotals({
					horse_total: r.data.horse_total,
					product_total: r.data.product_total,
					transport_total: r.data.transport_total,
					school_total: r.data.school_total,
					stable_total: r.data.stable_total,
					trainer_total: r.data.trainer_total,
					event_total: r.data.event_total,
					vet_total: r.data.vet_total,
					total: r.data.total
				});
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query, type, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearchParams({ q: query, type, page: '1' });
    setPage(1);
  };

  const switchType = (t) => {
    setType(t);
    setPage(1);
    setSearchParams({ q: query, type: t, page: '1' });
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    return Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
  };

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="search-results-page">
      <div className="bg-white border-b border-[#E6E2D6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#666055] hover:text-[#1A3C34] mb-4"
								data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </Link>
          <h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-4">
						{t('search.title')}
					</h1>
          <form onSubmit={handleSearch} className="flex gap-3 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666055]" />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('search.placeholder')}
                className="pl-11 h-12 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
              />
            </div>
            <Button type="submit" className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12 px-6"
										data-testid="search-submit">
              {t('common.search')}
            </Button>
          </form>
          {/* Type tabs */}
					<div className="flex items-center justify-between gap-3 mt-6 flex-wrap">
						<div className="flex gap-2" data-testid="search-type-tabs">
							{['all', 'horse', 'product', 'transport', 'school', 'stable', 'trainer', 'event', 'vet'].map(tab => (
								<button
									key={tab}
									onClick={() => switchType(tab)}
									data-testid={`tab-${tab}`}
									className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
										type === tab ? 'bg-[#1A3C34] text-white' : 'bg-[#F2F0E9] text-[#666055] hover:bg-[#E6E2D6]'
									}`}
								>
									{ resolveSearchTab(tab, totals, t) }
								</button>
							))}
						</div>
						<SaveSearchButton adType={ type } />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-sm border border-[#E6E2D6] overflow-hidden">
                <div className="aspect-[4/3] img-skeleton" />
                <div className="p-5 space-y-3">
                  <div className="h-5 w-3/4 img-skeleton rounded" />
                  <div className="h-7 w-1/3 img-skeleton rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20" data-testid="no-search-results">
            <Search className="w-12 h-12 mx-auto mb-4 text-[#D4C5A9]" />
            <p className="text-lg text-[#666055]">{query ? t('search.no_results') : t('search.enter_query')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-grid">
            {results.map((item) =>
							item.type === 'listing' ? (
								<Link
									key={item.id}
									to={ `/${ resolveUrl(item.result_type) }/${ item.slug || item.id }` }
									state={{ from: { path: location.pathname + location.search, label: t('search.title') } }}
									className="horse-card group bg-white border border-[#E6E2D6] rounded-sm overflow-hidden"
									data-testid={`search-item-${item.id}`}
								>
									<div className="aspect-[4/3] bg-[#F2F0E9] overflow-hidden relative">
										{item.images?.[0] ? (
											<img src={item.images[0].startsWith('http') ? item.images[0] : `${process.env.REACT_APP_BACKEND_URL}${item.images[0]}`} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
										) : (
											<div className="w-full h-full flex items-center justify-center text-[#D4C5A9]">
												{ item.result_type === 'horse' ?
													<Star className="w-12 h-12" />
													:
													<ShoppingBag className="w-12 h-12" />
												}
											</div>
										)}
										<span className={`absolute top-3 right-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${
											item.result_type === 'horse' ? 'bg-[#1A3C34] text-white' : 'bg-[#8B4513] text-white'
										}`}>
											{item.result_type}
										</span>
										{item.is_featured && (
											<span className="absolute top-3 left-3 featured-badge px-3 py-1 text-[10px] font-bold tracking-wider uppercase text-[#1A3C34] rounded-sm">
												{t('horse.featured')}
											</span>
										)}
									</div>
									<div className="p-5">
										<h3 className="text-lg font-semibold text-[#1A3C34] group-hover:text-[#8B4513] transition-colors line-clamp-1 font-['Playfair_Display']">
											{item.title}
										</h3>
										<p className="text-2xl font-bold text-[#8B4513] mt-1 mb-3">&euro;{item.price?.toLocaleString()}</p>
										<div className="flex flex-wrap items-center gap-3 text-xs text-[#666055]">
											{ item.breed &&
												<span className="px-2 py-0.5 bg-[#F2F0E9] rounded-sm">{breedMap[item.breed] || item.breed}</span>
											}
											{ item.category &&
												<span className="px-2 py-0.5 bg-[#F2F0E9] rounded-sm">
													{catMap[item.category] || item.category}
												</span>
											}
											{ item.date_of_birth &&
												<span>{calculateAge(item.date_of_birth)} {t('search.years')}</span>
											}
										</div>
										{item.location_name && (
											<div className="flex items-center gap-1 mt-3 text-xs text-[#666055]/70">
												<MapPin className="w-3 h-3" /> {item.location_name}
											</div>
										)}
									</div>
								</Link>
            	) : (
								<AdSlot
									key={item.id}
									id={item.id}
									provider="gam"
									placement="search_results_infeed"
								/>
							)
						)}
          </div>
        )}

        {/* Pagination */}
        {totals.total > 12 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="search-pagination">
            <Button disabled={page <= 1}
										onClick={() => { setPage(p => p - 1); setSearchParams({ q: query, type, page: String(page - 1) }); }}
										variant="outline" className="border-[#E6E2D6] rounded-sm h-10 w-10 p-0">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#666055] px-3">Page {page}</span>
            <Button disabled={results.length < 12}
										onClick={() => { setPage(p => p + 1); setSearchParams({ q: query, type, page: String(page + 1) }); }}
										variant="outline" className="border-[#E6E2D6] rounded-sm h-10 w-10 p-0">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
